import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import LocalFireDepartmentOutlinedIcon from '@mui/icons-material/LocalFireDepartmentOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
// eslint-disable-next-line no-unused-vars -- reserved for religion guardrail (commented out below)
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import FavoriteBorderOutlinedIcon from '@mui/icons-material/FavoriteBorderOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { CallWithAuth } from '../../../actions/apiAction';

export const GUARDRAIL_ITEMS = [
    {
        id: 'politics',
        label: 'Politics',
        sub: 'Decide if your twin can discuss politics',
        Icon: AccountBalanceOutlinedIcon,
        iconBg: '#e83e9c0f',
        iconColor: '#185FA5',
        defaultOn: false,
    },
    // {
    //     id: 'religion',
    //     label: 'Religion',
    //     sub: 'Faith-forward and devotional content',
    //     Icon: AutoAwesomeOutlinedIcon,
    //     iconBg: '#EEEDFE',
    //     iconColor: '#534AB7',
    //     defaultOn: true,
    // },
    {
        id: 'relationship_status',
        label: 'Relationship status',
        sub: 'Decide if your twin will discuss your relationship status',
        Icon: FavoriteBorderOutlinedIcon,
        iconBg: '#e83e9c0f',
        iconColor: '#C0392B',
        defaultOn: false,
    },
    {
        id: 'explicit',
        label: 'Explicit content',
        sub: 'Choose whether your twin responds to adult content',
        Icon: LocalFireDepartmentOutlinedIcon,
        iconBg: '#e83e9c0f',
        iconColor: '#993556',
        defaultOn: false,
    },
];

const DEFAULT_STATE = Object.fromEntries(GUARDRAIL_ITEMS.map(i => [i.id, i.defaultOn]));
const ALL_ON_STATE = Object.fromEntries(GUARDRAIL_ITEMS.map(i => [i.id, true]));
const ALL_OFF_STATE = Object.fromEntries(GUARDRAIL_ITEMS.map(i => [i.id, false]));

function Toggle({ on, onToggle, label, disabled }) {
    return (
        <button
            type="button"
            role="switch"
            aria-checked={on}
            aria-label={label}
            onClick={onToggle}
            disabled={disabled}
            style={{
                width: 46,
                height: 26,
                borderRadius: 13,
                border: 'none',
                background: on ? '#AF0056' : '#C0BCBA',
                position: 'relative',
                cursor: disabled ? 'not-allowed' : 'pointer',
                flexShrink: 0,
                transition: 'background 0.2s',
                padding: 0,
                outline: 'none',
                opacity: disabled ? 0.6 : 1,
            }}
        >
            <span
                style={{
                    position: 'absolute',
                    top: 3,
                    left: on ? 23 : 3,
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    background: '#fff',
                    transition: 'left 0.2s',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.18)',
                    display: 'block',
                }}
            />
        </button>
    );
}

function GuardrailCard({ item, on, onToggle, disabled }) {
    const { Icon } = item;
    return (
        <div
            onClick={disabled ? undefined : onToggle}
            style={{
                background: '#1d1d26',
                borderRadius: 14,
                padding: '14px 16px',
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                cursor: disabled ? 'not-allowed' : 'pointer',
                boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
            }}
        >
            {/* Icon */}
            <div
                style={{
                    width: 42,
                    height: 42,
                    borderRadius: 10,
                    background: item.iconBg,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                }}
            >
                <Icon style={{ fontSize: 22, color: item.iconColor }} />
            </div>

            {/* Text */}
            <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ margin: '0 0 2px', fontSize: 15, fontWeight: 600, color: '#ffffff' }}>
                    {item.label}
                </p>
                <p style={{ margin: 0, fontSize: 13, }}>
                    {item.sub}
                </p>
            </div>

            {/* Toggle */}
            <Toggle
                on={on}
                onToggle={(e) => { e.stopPropagation(); if (!disabled) onToggle(); }}
                label={item.label}
                disabled={disabled}
            />
        </div>
    );
}

export default function MyGuardrails() {
    const navigate = useNavigate();
    const [state, setState] = useState(DEFAULT_STATE);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);
    // Track the in-flight patch so concurrent toggles don't race
    const savingRef = useRef(false);

    useEffect(() => {
        CallWithAuth('GET', '/creator-dashboard/guardrails').then(res => {
            if (res?.data) {
                setState(prev => ({ ...prev, ...res.data }));
            }
        }).finally(() => setLoading(false));
    }, []);

    const patch = async (updates) => {
        if (savingRef.current) return;
        savingRef.current = true;
        setSaving(true);
        const res = await CallWithAuth('PATCH', '/creator-dashboard/guardrails', updates);
        savingRef.current = false;
        setSaving(false);
        return res;
    };

    const toggle = async (id) => {
        const newVal = !state[id];
        setState(prev => ({ ...prev, [id]: newVal })); // optimistic update
        const res = await patch({ [id]: newVal });
        if (!res?.data) {
            // Revert on failure
            setState(prev => ({ ...prev, [id]: !newVal }));
        }
    };

    const allOn = GUARDRAIL_ITEMS.every(item => state[item.id]);

    const toggleAll = async () => {
        const target = allOn ? ALL_OFF_STATE : ALL_ON_STATE;
        setState(target);
        const res = await patch(target);
        if (!res?.data) {
            // Reload from server on failure
            CallWithAuth('GET', '/creator-dashboard/guardrails').then(r => {
                if (r?.data) setState(prev => ({ ...prev, ...r.data }));
            });
        }
    };
    return (
        <>
            <Helmet>
                <title>My Guardrails</title>
            </Helmet>
            <div className="main-body">
                <div className="main-header-s1 center-text">
                    <button type="button" className="back-btn" onClick={() => { navigate(-1) }}>
                        <img src="/images/icons/chevron-left.svg" alt="" />
                    </button>
                    <h1 className="main-header-s1__title">My Guardrails</h1>

                    <div className="main-header-s1__actions"></div>
                </div>
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                }}>


                    {/* Body */}
                    <div style={{ flex: 1, padding: '12px 16px 100px' }}>
                        <h4 style={{
                            fontSize: 13,
                            fontWeight: 800,
                            color: '#fff',
                            letterSpacing: '0.08em',
                            textTransform: 'uppercase',
                            margin: '8px 4px 12px',
                        }}>
                            Content Controls
                        </h4>
                        <p style={{
                            fontSize: 13,
                            fontWeight: 400,
                            color: '#fff',
                            margin: '0 4px 8px',
                        }}
                        >
                            Decide what your twin will discuss
                        </p>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            {loading
                                ? GUARDRAIL_ITEMS.map(item => (
                                    <div key={item.id} style={{
                                        background: '#1d1d26',
                                        borderRadius: 14,
                                        height: 70,
                                        animation: 'pulse 1.5s ease-in-out infinite',
                                    }} />
                                ))
                                : GUARDRAIL_ITEMS.map(item => (
                                    <GuardrailCard
                                        key={item.id}
                                        item={item}
                                        on={state[item.id]}
                                        onToggle={() => toggle(item.id)}
                                        disabled={saving}
                                    />
                                ))
                            }
                        </div>

                        <p style={{

                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 8,
                            margin: '16px 4px 0',
                        }}>
                            <WarningAmberOutlinedIcon
                                sx={{ fontSize: 20, flexShrink: 0, marginTop: 1, color: '#D97706', fill: '#D97706' }}

                            />
                            <span style={{
                                fontSize: 15,
                                lineHeight: 1.5,
                            }}>
                                These settings work best when configured upfront. Toggling them frequently or mid-conversation will confuse your twin and produce unwanted AI behavior.
                            </span>
                        </p>
                    </div>

                    {/* Footer */}
                    <div style={{
                        position: 'fixed',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        padding: '12px 16px 28px',
                        // background: 'linear-gradient(to top, rgba(220,212,232,1) 60%, transparent)',
                    }}>
                        <button
                            type="button"
                            onClick={toggleAll}
                            disabled={saving || loading}
                            style={{
                                width: '100%',
                                padding: '14px',
                                borderRadius: 14,
                                border: allOn ? '1.5px solid #333' : 'none',
                                background: allOn ? '#fff' : '#AF0056',
                                fontSize: 15,
                                fontWeight: 600,
                                color: allOn ? '#111' : '#fff',
                                cursor: saving || loading ? 'not-allowed' : 'pointer',
                                letterSpacing: '0.01em',
                                opacity: saving || loading ? 0.6 : 1,
                            }}
                        >
                            {allOn ? 'DISABLE ALL' : 'ENABLE ALL'}
                        </button>
                    </div>
                </div>

                <style>{`
                    @keyframes spin { to { transform: rotate(360deg); } }
                    @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
                `}</style>
            </div>
        </>
    );
}
